import { Box, BoxProps, Center, SimpleGrid, Text } from '@chakra-ui/react'
import {
  compareAsc,
  eachDayOfInterval,
  format,
  getDay,
  isAfter,
  isEqual,
  isToday,
} from 'date-fns'
import { Options, Returns } from 'use-lilius'

import { useIsDark } from '../../hooks/useIsDark'

type LilusTypes = Returns & Options

type RangeSelectCalendarProps = BoxProps & {
  lilus: LilusTypes
  onUpdate?: ({
    toDate,
    fromDate,
  }: {
    toDate?: Date | undefined
    fromDate?: Date | undefined
  }) => void
  date: Date
}

export const RangeSelectCalendar = ({
  lilus,
  onUpdate = () => {},
}: RangeSelectCalendarProps) => {
  const isDark = useIsDark()
  const {
    calendar,
    isSelected,
    select,
    selected,
    setSelected,
    deselect,
    selectRange,
  } = lilus

  //todo: this needs some cleanup... maybe
  const clipPath = (day: Date) => {
    if (selected.length > 1) {
      const isSelectedDatesSequential = isAfter(
        selected[0],
        selected[selected.length - 1],
      )
      if (isSelectedDatesSequential) {
        if (isEqual(selected[0], day)) {
          return 'polygon(0 0, 50% 0, 50% 100%, 0% 100%)'
        }
        if (isEqual(selected[selected.length - 1], day)) {
          return 'polygon(100% 0, 50% 0, 50% 100%, 100% 100%)'
        }
      } else {
        if (isEqual(selected[0], day)) {
          return 'polygon(100% 0, 50% 0, 50% 100%, 100% 100%)'
        }
        if (isEqual(selected[selected.length - 1], day)) {
          return 'polygon(0 0, 50% 0, 50% 100%, 0% 100%)'
        }
      }
    }
    return 'none'
  }

  // tricky way to get the month that is in view depending on which CAL we are rendering
  const isDayWithinViewingMonth = (day: Date, month: Date[][]) =>
    month[3][3].getMonth() === day.getMonth()

  const monthGridHeight = (weeks: number) => 31 + weeks * 31 + weeks * 4 // header + date rows + gaps
  const outerHeight = Math.max(
    ...calendar.map(month => monthGridHeight(month.length)),
  )
  const gapX = 10
  const barExtension = {
    w: 31 + gapX,
    h: 31,
    left: -gapX / 2,
    top: 0,
  }

  const isInSelectedRange = (day: Date) => {
    if (selected.length < 2) return false
    const [start, end] = [selected[0], selected[selected.length - 1]].sort(
      (a, b) => compareAsc(a, b),
    )
    return compareAsc(day, start) >= 0 && compareAsc(day, end) <= 0
  }

  return (
    <SimpleGrid
      columns={2}
      spacing="20px"
      marginBottom="10px"
      height={`${outerHeight}px`}
    >
      {calendar.map(month => (
        <SimpleGrid
          key={`month-${month}`}
          gap="4px 10px"
          w="100%"
          templateColumns="repeat(7, minmax(0, 1fr))"
          gridAutoRows="1fr"
          height={`${monthGridHeight(month.length)}px`}
        >
          {month[0].map(day => (
            <Box
              key={`month-${month}-day${day}`}
              alignContent="center"
              justifyContent="center"
              height="31px"
            >
              <Center>
                <Text
                  color="presets.neutral.70"
                  fontWeight="600"
                  fontSize="12px"
                >
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'][getDay(day)]}
                </Text>
              </Center>
            </Box>
          ))}
          {month.map(week => {
            return week.map(day => {
              // const isInRange = inRange(
              //   day,
              //   startOfMonth(viewing),
              //   endOfMonth(viewing),
              // )
              return (
                <Box
                  position="relative"
                  width="31px"
                  height="31px"
                  overflow="visible"
                  data-selected={isSelected(day)}
                  key={`${day}`}
                  cursor="pointer"
                  onClick={() => {
                    if (!isDayWithinViewingMonth(day, month)) {
                      return
                    }
                    const sorted = selected.sort((a, b) => compareAsc(a, b))

                    if (sorted.length === 0 || selected.length > 2) {
                      setSelected([])
                      select(day)
                      onUpdate({ fromDate: day })
                    } else if (isSelected(day)) {
                      if (selected.length === 1) {
                        deselect(day)
                        onUpdate({})
                      } else {
                        const range = eachDayOfInterval({
                          start: sorted[0],
                          end: day,
                        })
                        const diff = sorted.filter(d =>
                          range.map(a => a.getTime()).includes(d.getTime()),
                        )

                        selectRange(diff[0], diff[diff.length - 1], true)
                        const isInOrder = isAfter(diff[0], day)
                        onUpdate({
                          toDate: isInOrder ? diff[0] : day,
                          fromDate: !isInOrder ? diff[0] : day,
                        })
                      }
                    } else {
                      selectRange(sorted[0], day, true)
                      const isInOrder = isAfter(sorted[0], day)
                      onUpdate({
                        toDate: isInOrder ? sorted[0] : day,
                        fromDate: !isInOrder ? sorted[0] : day,
                      })
                    }
                  }}
                  alignContent="center"
                  justifyContent="center"
                >
                  <Box
                    background={
                      isInSelectedRange(day) &&
                      isDayWithinViewingMonth(day, month)
                        ? isDark
                          ? 'presets.turquoise.80'
                          : 'presets.turquoise.20'
                        : 'none'
                    }
                    width={`${barExtension.w}px`}
                    height={`${barExtension.h}px`}
                    left={`${barExtension.left}px`}
                    top={`${barExtension.top}px`}
                    clipPath={
                      isSelected(day) && selected.length > 1
                        ? clipPath(day)
                        : 'none'
                    }
                    position="absolute"
                  />

                  <Box
                    background={
                      isDayWithinViewingMonth(day, month) &&
                      (isEqual(selected[0], day) ||
                        isEqual(selected[selected.length - 1], day))
                        ? isDark
                          ? 'presets.turquoise.50'
                          : 'presets.neutral.100'
                        : 'none'
                    }
                    height="31px"
                    position="relative"
                    rounded={
                      isEqual(selected[0], day) ||
                      isEqual(selected[selected.length - 1], day)
                        ? 'full'
                        : 'none'
                    }
                  >
                    <Center
                      height="31px"
                      _hover={{
                        ...(isDayWithinViewingMonth(day, month) &&
                          day.toDateString() !== selected[0]?.toDateString() &&
                          day.toDateString() !==
                            selected[selected.length - 1]?.toDateString() && {
                            background: isDark
                              ? 'presets.turquoise.50'
                              : 'presets.turquoise.10',
                            border: isDark ? 'none' : '1px solid darkgreen',
                            rounded: 'full',
                            height: '31px',
                            width: '31px',
                          }),
                      }}
                    >
                      <Text
                        fontWeight={isInSelectedRange(day) ? '600' : '500'}
                        fontSize="14px"
                        color={
                          isEqual(selected[0], day) ||
                          isEqual(selected[selected.length - 1], day)
                            ? 'white'
                            : isDark && isInSelectedRange(day)
                              ? 'presets.neutral.0'
                              : 'presets.turquoise.100'
                        }
                      >
                        {isDayWithinViewingMonth(day, month)
                          ? `${format(day, 'd')}`
                          : ''}
                      </Text>
                    </Center>
                    <Box
                      background={
                        isDark
                          ? 'presets.turquoise.50'
                          : isSelected(day)
                            ? 'white'
                            : 'black'
                      }
                      position="absolute"
                      width="6px"
                      height="6px"
                      left="50%"
                      top="100%"
                      transform="translate(-50%, 4px)"
                      rounded="full"
                      hidden={
                        !isToday(day) || !isDayWithinViewingMonth(day, month)
                      }
                    />
                  </Box>
                </Box>
              )
            })
          })}
        </SimpleGrid>
      ))}
    </SimpleGrid>
  )
}
